import { Injectable } from "@angular/core";
import { File } from "@ionic-native/file";
import { FilePath } from "@ionic-native/file-path";
import { IOSFilePicker } from "@ionic-native/file-picker";
import { FileTransfer, FileTransferObject, FileUploadOptions } from "@ionic-native/file-transfer";
import async from 'async';
import { Platform } from "ionic-angular";
import moment from "moment";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { Subscription } from "rxjs/Subscription";
import { FileDownloaderProvider } from "../../../providers/file-downloader/file-downloader";
import { FileUploaderProvider, OptionsUploadWithHttp } from "../../../providers/file-uploader/file-uploader";
import { GlobalServiceProvider } from "../../../providers/global-service/global-service";
import { StoragePathProvider } from "../../../providers/storage-path/storage-path";
import { RequestSetCommentFeedbackDto, RequestSetStatusTaskDto, RequestSetTaskDto } from "../../../shared/nestjs/gd-all-in-one/dto/task-feedback.dto";
import { TaskFeedbackApi } from "../../../shared/nestjs/gd-all-in-one/services/task-feedback.api";
import { RequestEmpListESSDto, ResponseEmpListESSDto } from "../../../shared/nestjs/gd-integrated/dto/request-for.dto";
import { RequestForApi } from "../../../shared/nestjs/gd-integrated/services/request-for.api";
import { ResponseProfileDto } from "../../../shared/nestjs/gd-only/dto/auth.dto";
import { AuthApi } from "../../../shared/nestjs/gd-only/services/auth.api";
import { HomeFeedProvider } from "../../../shared/nestjs/home-feed/home-feed";
import { NestApiProvider } from "../../../shared/nestjs/nest-api";
import { SfrequestforApi, SunfishApi, TaskFeedbackServiceApi } from "../../../shared/sdk";
import { CameraNative } from "../../../native/camera.native";
import { ResponseGetTodayTaskDto } from "../../../shared/nestjs/gd-only/dto/home-feed.dto";
import { GamificationProvider } from "../../../providers/gamification/gamification";
import { badgeModule } from "../../../shared/nestjs/gd-only/dto/gamification.dto";
import { AppPreferences } from "../../../shared/local/app-preferences.local";
import { FileChooserProvider } from "../../../providers/file-chooser/file-chooser";
import { AnalyticsProvider } from "../../../providers/analytics/analytics";
import { EventNameAnalytics } from "../../../helpers/event-name-analytics";

@Injectable()
export class TaskAndFeedbackBloc {
  private subscriptions: Array<Subscription> = [];
  private supportFile = ['pdf', 'xlsx', 'xls', 'docx', 'doc', 'pptx', 'ppt']
  private _todayTask: ResponseGetTodayTaskDto[] = [];
  private _finishLoadMoreTodayTask = false;
  private _loadingGetTodayTask = false;

  constructor(
    private taskFeedbackApi: TaskFeedbackApi,
    private taskFeedbackServiceApi: TaskFeedbackServiceApi,
    private nestApiProvider: NestApiProvider,
    private globalServiceProvider: GlobalServiceProvider,
    private homeFeedProvider: HomeFeedProvider,
    private filePath: FilePath,
    private transfer: FileTransfer,
    private platform: Platform,
    private storagePathProvider: StoragePathProvider,
    private fileChooserProvider: FileChooserProvider,
    private iosFilePicker: IOSFilePicker,
    private requestForApi: RequestForApi,
    private sfrequestforApi: SfrequestforApi,
    private authApi: AuthApi,
    private sunfishApi: SunfishApi,
    private fileUploaderProvider: FileUploaderProvider,
    private fileDownloaderProvider: FileDownloaderProvider,
    private cameraNative: CameraNative,
    private gamificationProvider: GamificationProvider,
    private appPreferences: AppPreferences,
    private analyticsProvider: AnalyticsProvider
  ) {
  }

  dispose() {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  getTask(payload: RequestTaskFeedbackPagingDto) {
    return new Promise<Array<any>>((resolve, reject) => {
      this.subscriptions.push(
        this.apiGetTask(payload)
          .subscribe(async (result) => {
            const data = await this.globalServiceProvider.convertArrObjToCamelCase(result);
            resolve(data);
          }, error => {
            reject(error)
          })
      )
    })
  }

  private apiGetTask(payload: RequestTaskFeedbackPagingDto): Observable<any> {
    payload.page = (payload.skip / payload.limit) + 1;
    return Observable.fromPromise(this.nestApiProvider.isGoPathNewExist())
      .mergeMap(isGoPathNewExist => {
        if (isGoPathNewExist) {
          return this.taskFeedbackApi.getTask(payload.limit, payload.page, false, payload.type).pipe(map(res => res.data));
        } else {
          return this.taskFeedbackServiceApi.getTask({ type: payload.type, limit: payload.limit, skip: payload.skip }).pipe(map(res => res.data));
        }
      })
  }

  getFeedback(payload: RequestTaskFeedbackPagingDto) {
    return new Promise<Array<any>>((resolve, reject) => {
      this.subscriptions.push(
        this.apiGetFeedback(payload)
          .subscribe(async (result) => {
            const data = await this.globalServiceProvider.convertArrObjToCamelCase(result);
            resolve(data);
          }, error => {
            reject(error)
          })
      )
    })
  }

  private apiGetFeedback(payload: RequestTaskFeedbackPagingDto): Observable<any> {
    payload.page = (payload.skip / payload.limit) + 1;
    return Observable.fromPromise(this.nestApiProvider.isGoPathNewExist())
      .mergeMap(isGoPathNewExist => {
        if (isGoPathNewExist) {
          return this.taskFeedbackApi.getFeedback(payload.limit, payload.page, payload.type).pipe(map(res => res.data));
        } else {
          return this.taskFeedbackServiceApi.getFeedback({ type: payload.type, limit: payload.limit, skip: payload.skip }).pipe(map(res => res.data));
        }
      })
  }

  sendFeedback(payload: RequestSendFeedbackDto) {
    return new Promise((resolve, reject) => {
      this.subscriptions.push(
        this.apiSendFeedback(payload).subscribe((result) => {
          this.analyticsProvider.trackEvent(EventNameAnalytics.TASK_AND_FEEDBACK_CREATE_FEEDBACK);
          resolve(result);
        }, error => {
          reject(error);
        })
      )
    })
  }

  private apiSendFeedback(payload: RequestSendFeedbackDto): Observable<any> {
    return Observable.fromPromise(this.nestApiProvider.isGoPathNewExist())
      .mergeMap(isGoPathNewExist => {
        if (isGoPathNewExist) {
          return this.taskFeedbackApi.setFeedbackTask(payload);
        } else {
          return this.taskFeedbackServiceApi.setFeedbackTask(payload);
        }
      })
  }

  saveStatusTask(picture: any[], payload: RequestSetStatusTaskDto) {
    return new Promise<any>(async (resolve, reject) => {
      try {
        if (picture.length > 0) {
          let pictureList = [];
          async.eachOfSeries(picture, async (item, key, callback) => {
            try {
              const fileName = await this.uploadMultiple(item);
              pictureList.push(fileName);
            } catch (error) {
            }

            callback();
          }, async (error) => {
            if (error) reject(error);

            if (pictureList.length > 0) {
              payload.attachments = pictureList;
              const setStatusTask = await this.setStatusTask(payload);
              resolve(setStatusTask);
            } else {
              reject("Failed set task");
            }
          })
        }
        else {
          payload.attachments = [];
          const setStatusTask = await this.setStatusTask(payload);
          resolve(setStatusTask);
        }
      }
      catch (error) {
        reject(error);
      }
    })
  }

  setStatusTask(payload: RequestSetStatusTaskDto) {
    return new Promise<Array<any>>((resolve, reject) => {
      this.subscriptions.push(
        this.apiSetStatusTask(payload)
          .subscribe(async (result) => {
            resolve(result);
          }, error => {
            reject(error)
          })
      )
    })
  }

  private apiSetStatusTask(payload: RequestSetStatusTaskDto): Observable<any> {
    return Observable.fromPromise(this.nestApiProvider.isGoPathNewExist())
      .mergeMap(isGoPathNewExist => {
        if (isGoPathNewExist) {
          return this.taskFeedbackApi.setStatusTask(payload)
        } else {
          return this.taskFeedbackServiceApi.setStatusTask({
            taskCode: payload.taskCode,
            statusTask: payload.status,
            completionDate: payload.completionDate,
            attachments: payload.attachments
          });
        }
      })
  }

  saveTask(picture: any[], payload: RequestSetTaskDto) {
    return new Promise<any>(async (resolve, reject) => {
      try {
        if (picture.length > 0) {
          let pictureList = [];
          async.eachOfSeries(picture, async (item, key, callback) => {
            try {
              const fileName = await this.uploadMultiple(item);
              pictureList.push(fileName);
            }
            catch (error) {
            }

            callback();
          }, async (error) => {
            if (error) reject(error);

            if (pictureList.length > 0) {
              payload.attachments = pictureList;
              const setTask = await this.setTask(payload);
              resolve(setTask);
            }
            else {
              reject("Failed set task");
            }
          })
        }
        else {
          payload.attachments = [];
          const setTask = await this.setTask(payload);
          resolve(setTask);
        }

      }
      catch (error) {
        reject(error);
      }
    })
  }

  setTask(payload: RequestSetTaskDto) {
    return new Promise<Array<any>>((resolve, reject) => {
      this.subscriptions.push(
        this.apiSetTask(payload)
          .subscribe(async (result) => {
            this.analyticsProvider.trackEvent(EventNameAnalytics.TASK_AND_FEEDBACK_CREATE_TASK);
            resolve(result);
          }, error => {
            reject(error)
          })
      )
    })
  }

  private apiSetTask(payload: RequestSetTaskDto): Observable<any> {
    return Observable.fromPromise(this.nestApiProvider.isGoPathNewExist())
      .mergeMap(isGoPathNewExist => {
        if (isGoPathNewExist) {
          return this.taskFeedbackApi.setTask(payload)
        } else {
          return this.taskFeedbackServiceApi.setTask(payload);
        }
      })
  }

  sendComment(payload: RequestSetCommentFeedbackDto) {
    return new Promise((resolve, reject) => {
      this.subscriptions.push(
        this.apiSendComment(payload).subscribe((result) => {
          resolve(result);
        }, error => {
          reject(error)
        })
      )
    })
  }

  private apiSendComment(payload: RequestSetCommentFeedbackDto): Observable<any> {
    return Observable.fromPromise(this.nestApiProvider.isGoPathNewExist())
      .mergeMap(isGoPathNewExist => {
        if (isGoPathNewExist) {
          return this.taskFeedbackApi.setCommentFeedback(payload)
        } else {
          return this.taskFeedbackServiceApi.setCommentFeedback(payload)
        }
      })
  }

  getUserData() {
    return new Promise<ResponseProfileDto>((resolve, reject) => {
      this.apiGetUserData().subscribe((result) => {
        resolve(result)
      }, error => {
        reject(error);
      })
    })
  }

  private apiGetUserData(): Observable<any> {
    return Observable.fromPromise(this.nestApiProvider.isGoPathNewExist())
      .mergeMap(isGoPathNewExist => {
        if (isGoPathNewExist) {
          return this.authApi.profile().pipe(map(response => response.data))
        } else {
          return this.sunfishApi.getUserData().pipe(map(async (response) => {
            const data = await this.globalServiceProvider.convertObjToCamelCase(response.result);
            return data
          }))
        }
      })
  }

  getEmployeeList(payload: RequestEmpListESSDto, isSF6) {
    return new Promise<ResponseEmpListESSDto>(async (resolve, reject) => {
      try {
        let getEmployee: ResponseEmpListESSDto;

        if (isSF6) {
          getEmployee = await this.getTaskMemberSF6(payload);
        }
        else {
          getEmployee = await this.getTaskMember(payload)
        }

        resolve(getEmployee)
      }
      catch (error) {
        reject(error)
      }
    })
  }

  getTaskMemberSF6(payload: RequestEmpListESSDto) {
    return new Promise<ResponseEmpListESSDto>((resolve, reject) => {
      this.subscriptions.push(
        this.apiGetTaskMemberSF6(payload)
          .subscribe((result) => {
            resolve(result);
          }, error => {
            reject(error)
          })
      )
    })
  }

  getTaskMember(payload: RequestEmpListESSDto) {
    return new Promise<ResponseEmpListESSDto>((resolve, reject) => {
      this.subscriptions.push(
        this.apiGetTaskMember(payload)
          .subscribe((result) => {
            resolve(result);
          }, error => {
            reject(error)
          })
      )
    })
  }

  private apiGetTaskMemberSF6(payload: RequestEmpListESSDto): Observable<any> {
    return Observable.fromPromise(this.nestApiProvider.isGoPathNewExist())
      .mergeMap(isGoPathNewExist => {
        if (isGoPathNewExist) {
          return this.requestForApi.empListESS(payload.limit, payload.skip, payload.search, payload.module);
        } else {
          return this.sfrequestforApi.getEmployeeTask({ search: payload.search, companyId: payload.companyId, limit: payload.limit, skip: payload.skip })
            .pipe(map(response => {
              let result = { data: [], message: 'success' }
              // maping data sebelum di return, supaya format data nya standar
              if (response.db == 'MSSQL') {
                result.data = response.result.map((element: any, index) => {
                  element.fullName = element.name;
                  element.posNameEn = element.position;
                  return element;
                }).slice(payload.skip, (Number(payload.skip) + Number(payload.limit)))
              }
              else {
                result.data = response.result.map((element: any, index) => {
                  element.fullName = element.name;
                  element.posNameEn = element.position;
                  return element;
                })
              }

              return result
            }))
        }
      })
  }


  private apiGetTaskMember(payload: RequestEmpListESSDto): Observable<any> {
    payload.page = (Number(payload.skip) / Number(payload.limit)) + 1;
    return Observable.fromPromise(this.nestApiProvider.isGoPathNewExist())
      .mergeMap(isGoPathNewExist => {
        if (isGoPathNewExist) {
          return this.taskFeedbackApi.getTaskMembers(payload.limit, payload.page, payload.search)
        } else {
          return this.taskFeedbackServiceApi.getTaskMembers({
            limit: payload.limit,
            skip: payload.skip,
            filter: payload.search
          })
        }
      })
  }

  uploadMultiple(item) {
    return new Promise(async (resolve, reject) => {
      try {
        const storage = await this.storagePathProvider.getStoragePost();
        console.log("item upload multiple", item)
        const options: OptionsUploadWithHttp = {
          eventsWebview: item.eventsWebview,
          nativeURI: item.data,
          options: item.options,
          type: item.type
        }
        const upload = await this.fileUploaderProvider.uploadWithHttp(options, storage.urlUpload);
        resolve(upload);
      }
      catch (error) {
        reject(error);
      }
    })
  }

  uploadMultipleNative(item, urlUpload) {
    return new Promise<string>(async (resolve, reject) => {
      let options: FileUploadOptions;
      let filePath = null;
      const fileTransfer: FileTransferObject = this.transfer.create();
      let result = null;

      if (item.type == "image") {
        options = {
          fileKey: 'file',
          fileName: moment().valueOf() + '.jpg',
          chunkedMode: false,
          mimeType: 'image/jpg'
        };

        filePath = item.data;
      } else {
        let originalName = item.data.substring(item.data.lastIndexOf('/') + 1);
        let fileType = originalName.substring(originalName.lastIndexOf('.') + 1);
        options = {
          fileKey: 'file',
          fileName: moment().valueOf() + "." + fileType,
          chunkedMode: false,
          mimeType: "multipart/form-data",
        };

        filePath = await this.filePath.resolveNativePath(item.data);
      }


      fileTransfer.upload(filePath, urlUpload, options)
        .then((data) => {
          result = JSON.parse(data['response']).result.files.file[0].name;
          resolve(result);
        }).catch(error => {
          reject(error);
        })
    })
  }

  async download(fileName: string) {
    return new Promise<void>(async (resolve, reject) => {
      try {
        const storageUrl = await this.storagePathProvider.getStoragePost();
        await this.fileDownloaderProvider.downloadWithHttp(storageUrl.urlDownload, fileName);
        resolve();
      }
      catch (error) {
        reject(error);
      }
    });
  }

  takePicture(isCamera) {
    return new Promise<ResponseTakeFileDto>(async (resolve, reject) => {
      const options = {
        quality: 50,
        sourceType: isCamera,
        allowEdit: false,
        targetWidth: 600,
        targetHeight: 600,
        cameraDirection: 0
      }

      try {
        const imageData = await this.cameraNative.getPicture(options);
        if (imageData.cancelled) {
          reject('Cancelled')
        }

        const result = {
          data: imageData.nativeURL,
          options: "",
          isSelected: false,
          eventsWebview: "",
          type: 'image'
        }

        resolve(result);
      }
      catch (error) {
        console.log('error take img', error);
        reject(error)
      }
    })
  }

  takeFile() {
    return new Promise<ResponseTakeFileDto>(async (resolve, reject) => {
      try {
        let result = null
        if (this.platform.is('android')) {
          result = await this.takeFileAndroid();
        }
        else {
          result = await this.takeFileIOS();
        }
        resolve(result);
      }
      catch (error) {
        reject(error);
      }
    })
  }

  takeFileAndroid() {
    return new Promise<ResponseTakeFileDto>(async (resolve, reject) => {
      try {
        const filePath = await this.fileChooserProvider.open();
        const type = this.getFileExtension(filePath);
        let checkSupportFile = this.supportFile.findIndex(item => item == type);

        if (checkSupportFile < 0) {
          reject('File not support');
        }

        const result = {
          data: filePath,
          options: "",
          isSelected: false,
          eventsWebview: "",
          type: 'file'
        }

        resolve(result);
      }
      catch (error) {
        reject(error);
      }
    })
  }

  takeFileIOS() {
    return new Promise<ResponseTakeFileDto>(async (resolve, reject) => {
      try {
        this.iosFilePicker.pickFile().then(filePath => {
          const result = {
            data: filePath,
            options: "",
            isSelected: false,
            eventsWebview: "",
            type: 'file'
          }
          resolve(result)
        })
      } catch (error) {
        reject(error)
      }
    })
  }

  processWebImage(event) {
    return new Promise((resolve) => {
      let name = event.target.files[0].name;
      let fileIMG = ['png', 'jpg', 'jpeg', 'bmp', 'gif'];
      let formatImage = name.split('.').pop();
      let res = formatImage.toLowerCase();
      let options = moment().valueOf() + '.' + res;
      let type = "image";
      if (fileIMG.indexOf(res) < 0) {
        type = "file"
      }

      let base: any;
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        base = reader.result;
        const result = {
          data: base,
          options: options,
          isSelected: false,
          eventsWebview: <Array<File>>event.target.files,
          type: type
        }

        resolve(result);
      }, false);
      reader.readAsDataURL(event.target.files[0]);
    })
  }
  /**
  * Load Status
  */
  loadStatus(val) {
    let name = 'New';
    switch (val) {
      case 0:
        name = 'New';
        break;
      case 1:
        name = 'In Progress';
        break;
      case 2:
        name = 'Completed';
        break;
      case 3:
        name = 'Verified';
        break;
      case 4:
        name = 'Cancelled';
        break;
      case 5:
        name = 'Suspended';
        break;
      case 6:
        name = 'Closed';
        break;
      default:
        name = 'New';
        break;
    }
    return name;
  }

  /**
   * Load Type
   */
  loadType(val) {
    let name = '';
    switch (val) {
      case 'compliment':
        name = 'Compliment';
        break;
      case 'complaint':
        name = 'Complaint';
        break;
      case 'neutral':
        name = 'Neutral';
        break;
      default:
        name = '';
        break;
    }
    return name;
  }

  /**
   * Load Rating
   */
  loadRating(val): any {
    let name = '';
    switch (val) {
      case 1:
        name = 'Poor';
        break;
      case 2:
        name = 'Bad';
        break;
      case 3:
        name = 'Fair';
        break;
      case 4:
        name = 'Good';
        break;
      case 5:
        name = 'Excellent';
        break;
      default:
        name = 'Poor';
        break;
    }
    return name;
  }

  /**
   * Load Severity
   */
  loadSeverity(val) {
    let name = '';
    switch (val) {
      case 1:
        name = 'Minor';
        break;
      case 2:
        name = 'Moderate';
        break;
      case 3:
        name = 'Major';
        break;
      default:
        name = '';
        break;
    }
    return name;
  }

  /**
   * Load Priority
   */
  loadPriority(val) {
    let name = 'Low';
    switch (val) {
      case 1:
        name = 'Low';
        break;
      case 2:
        name = 'Normal';
        break;
      case 3:
        name = 'High';
        break;
      default:
        name = 'Low';
        break;
    }
    return name;
  }

  /**
   * Load
   */
  loadFeedbackStatus(type, level) {
    let name = '';
    switch (type) {
      case 'complaint':
        switch (level) {
          case 1:
            name = "Fair"
            break;
          case 2:
            name = "Poor"
            break;
          case 3:
            name = "Very Poor"
            break;
          default:
            name = "Fair"
            break;
        }
        break;
      case 'compliment':
        switch (level) {
          case 1:
            name = "Good"
            break;
          case 2:
            name = "Very Good"
            break;
          case 3:
            name = "Excellent"
            break;
          default:
            name = "Good"
            break;
        }
        break;
      default:
        name = 'Neutral';
        break;
    }
    console.log(name);
    return name;
  }

  getDetailTaskById(id) {
    return new Promise((resolve, reject) => {
      this.homeFeedProvider.getIdTask(id)
        .subscribe(result => {
          resolve(result)
        }, error => {
          reject(error);
        });
    })
  }

  setLabelStatus(statusNumber: number) {
    let status = '';
    if (statusNumber == 0) {
      status = "New";
    } else if (statusNumber == 1) {
      status = "In Progress";
    } else if (statusNumber == 2) {
      status = "Completed";
    } else if (statusNumber == 3) {
      status = "Verified";
    } else if (statusNumber == 4) {
      status = "Cancelled";
    } else if (statusNumber == 5) {
      status = "Suspend";
    } else if (statusNumber == 6) {
      status = "Closed";
    } else {
      status = "";
    }

    return status;
  }

  getFileType(relImages: Array<any>) {
    let tempData = [];
    if (relImages.length > 0) {
      for (const task of relImages) {
        if (
          task.imagesName.includes('.doc') ||
          task.imagesName.includes('.docx') ||
          task.imagesName.includes('.pptx') ||
          task.imagesName.includes('.ppt') ||
          task.imagesName.includes('.xls') ||
          task.imagesName.includes('.xlsx') ||
          task.imagesName.includes('.txt') ||
          task.imagesName.includes('.pdf')
        ) {
          task.type = 'file'
        } else {
          task.type = 'image'
        }

        tempData.push(task);
      }
    }

    return tempData
  }

  getFileExtension(filename) {
    if (filename) {
      try {
        return filename.slice((filename.lastIndexOf(".") - 1 >>> 0) + 2);
      } catch {
        return 'file'
      }
    }
  }

  getIconFile(filename, options) {

    // console.log('filename', filename);
    // console.log('options', options);
    let fileType = this.getFileExtension(filename);
    let fileOpt = this.getFileExtension(options);

    // console.log('fileType', fileType);
    // console.log('fileOpt', fileOpt);
    let result = 'assets/images/chat/attach.svg';

    if (fileType == 'pdf' || fileOpt == 'pdf') {
      result = 'assets/images/file/PDF.svg';
    } else if (fileType == 'xlsx' || fileOpt == 'xlsx') {
      result = 'assets/images/file/XLSX.svg';
    } else if (fileType == 'docx' || fileOpt == 'docx') {
      result = 'assets/images/file/DOCX.svg';
    } else if (fileType == 'pptx' || fileOpt == 'pptx') {
      result = 'assets/images/file/PPTX.svg';
    } else if (fileType == 'xls' || fileOpt == 'xls') {
      result = 'assets/images/file/XLS.svg';
    } else if (fileType == 'doc' || fileOpt == 'doc') {
      result = 'assets/images/file/DOC.svg';
    } else if (fileType == 'ppt' || fileOpt == 'ppt') {
      result = 'assets/images/file/PPT.svg';
    }

    return result;
  }

  getTodayTask(page = 1, limit = 5, infinite: boolean = false): Promise<any> {
    return new Promise((resolve) => {
      this._loadingGetTodayTask = true;
      this.homeFeedProvider.getTodayTask(page, limit)
        .subscribe((result) => {
          if (infinite) {
            result.data.map(item => this._todayTask.push(item));
          } else {
            this._todayTask = result.data;
          }

          this._finishLoadMoreTodayTask = result.page === result.totalPage;
          this._loadingGetTodayTask = false;
          resolve(this._todayTask);
        }, error => {
          this._loadingGetTodayTask = false;
          resolve(null);
        });
    });
  }

  get loadingGetTodayTask() {
    return this._loadingGetTodayTask;
  }

  get finishLoadMoreTodayTask() {
    return this._finishLoadMoreTodayTask;
  }

  get todayTask() {
    return this._todayTask;
  }

  updateTask(taskCode, newStatusTask, newStatus) {
    return new Promise((resolve, reject) => {
      this.homeFeedProvider.updateTask(taskCode, newStatusTask, newStatus)
        .subscribe(async (result) => {
          const authGamification = this.appPreferences.dataAuth.menu["gamification"] || false;
          if (newStatusTask == 2 && authGamification) {
            await this.gamificationProvider.checkAchievement(badgeModule.taskFinisher);
          }

          resolve(result);
        }, error => {
          reject(error);
        })
    })
  }
}

export interface RequestTaskFeedbackPagingDto {
  limit: number;
  page?: number;
  type: any;
  skip: number
}

export interface ResponseTakeFileDto {
  data: string,
  options: any,
  isSelected: boolean,
  eventsWebview: any,
  type: string
}

export interface RequestSendFeedbackDto {
  taskCode: string,
  feedbackFor: any,
  rating: any,
  remark: any,
}



// WEBPACK FOOTER //
// ./src/bloc/feature/task-and-feedback/task-and-feedback.bloc.ts